/**
 * Draft List Component
 *
 * Displays list of draft rules with status indicators and selection.
 * Styled to match JotAudit design patterns.
 */

import React, { useCallback } from 'react';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  SaveAlt as SaveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { DraftRuleResponse } from '@store/generated/rule-build';

import { DRAFT_STATUS_LABELS, SOURCE_TYPE_LABELS } from '../constants';
import { useAddDraftMutation } from '../store';
import type { DraftSourceType,DraftStatus } from '../types';

export interface DraftListProps {
  drafts: DraftRuleResponse[];
  selectedDraftId: string | null;
  onSelectDraft: (draftId: string) => void;
  taskId: string;
  isLoading?: boolean;
}

/**
 * Get status display info
 */
const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'ready':
      return {
        icon: CheckIcon,
        color: '#22C55E', // Green
        bgColor: '#22C55E15',
        label: 'Ready',
      };
    case 'saved':
      return {
        icon: SaveIcon,
        color: '#2196F3', // Blue
        bgColor: '#2196F315',
        label: 'Saved',
      };
    default:
      return {
        icon: EditIcon,
        color: '#6B7280', // Gray
        bgColor: '#6B728015',
        label: 'Draft',
      };
  }
};

export const DraftList: React.FC<DraftListProps> = ({
  drafts,
  selectedDraftId,
  onSelectDraft,
  taskId,
  isLoading = false,
}) => {
  const theme = useTheme();

  // Add draft mutation
  const [addDraft, { isLoading: isAdding }] = useAddDraftMutation();

  // Handle add new draft
  const handleAddDraft = useCallback(async () => {
    try {
      const result = await addDraft({
        taskId,
        addDraftRequest: {
          title: 'New Rule',
          body_md: '',
        },
      }).unwrap();
      // Select the new draft
      onSelectDraft(result.draft_id);
    } catch (err) {
      console.error('Failed to add draft:', err);
    }
  }, [addDraft, taskId, onSelectDraft]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: 0,
        borderRight: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'textColors.darkText',
          }}
        >
          Drafts ({drafts.length})
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddDraft}
          disabled={isAdding}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: 'backgroundColors.secondary',
            },
          }}
        >
          ADD
        </Button>
      </Box>

      {/* Draft list */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                sx={{height: 80,  borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : drafts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{ color: 'textColors.lightText', mb: 0.5 }}
            >
              No drafts yet.
            </Typography>
            <Typography variant="body2" sx={{ color: 'textColors.lightText' }}>
              Use the chat to generate rules.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {drafts.map((draft) => {
              const isSelected = draft.id === selectedDraftId;
              const statusDisplay = getStatusDisplay(draft.status);
              const StatusIcon = statusDisplay.icon;

              return (
                <Box
                  key={draft.id}
                  onClick={() => onSelectDraft(draft.id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isSelected
                      ? 'backgroundColors.secondary'
                      : theme.palette.background.paper,
                    border: '1px solid',
                    borderColor: isSelected
                      ? 'borderColors.brand'
                      : theme.palette.grey[200],
                    borderLeft: isSelected
                      ? `3px solid ${theme.palette.primary.main}`
                      : '1px solid',
                    borderLeftColor: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.grey[200],
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: isSelected
                        ? 'borderColors.brand'
                        : theme.palette.primary.main,
                      boxShadow: isSelected ? 0 : 1,
                    },
                  }}
                >
                  {/* Title row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: 'textColors.darkText',
                        lineHeight: 1.4,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {draft.title || 'Untitled Rule'}
                    </Typography>

                    {/* Status badge */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: statusDisplay.bgColor,
                        flexShrink: 0,
                      }}
                    >
                      <StatusIcon
                        sx={{
                          fontSize: 12,
                          color: statusDisplay.color,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          color: statusDisplay.color,
                          letterSpacing: '0.3px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {DRAFT_STATUS_LABELS[draft.status as DraftStatus] || draft.status}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Source type */}
                  {draft.source?.type && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'textColors.lightText',
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {SOURCE_TYPE_LABELS[draft.source.type as DraftSourceType] || draft.source.type}
                    </Typography>
                  )}

                  {/* Selector preview */}
                  {draft.selector_text && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'textColors.lightText',
                        fontSize: '0.7rem',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {draft.selector_text}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default DraftList;
